<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useDrawerStore } from '@/stores/drawer'
import { useCalculationStore } from '@/stores/calculation'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { storeToRefs } from 'pinia'
import type {
  CalculationFormTab,
  CalculationFormTabs
} from '@/components/calculation-form/type/type'
import { computed, onMounted, type PropType } from 'vue'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'
import { isDatabaseSwitchRecoveryTab } from '@/components/calculation-form/constants/databaseSwitchRecoveryTabs'
import {
  resolveDesignLinkUnitMismatch,
  resolveBuildingBlockUnit
} from '@/components/calculation-form/design-link/utils/designLinksUtils'

const props = defineProps({
  tabs: { type: Array as PropType<CalculationFormTabs>, required: true },
  hasBlockingSwitchIssues: { type: Boolean, required: false, default: false }
})

const tab = defineModel<CalculationFormTab>('tab', { required: true })

const router = useRouter()
const drawerStore = useDrawerStore()
const calculationStore = useCalculationStore()
const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
const buildingBlockStore = useBuildingBlockStore()
const confirmDeleteStore = useConfirmDeleteStore()

const { drawer } = storeToRefs(drawerStore)
const { currentItem: currentCalculationItem } = storeToRefs(calculationStore)
const { items: quantityBuildingBlockItems } = storeToRefs(quantityBuildingBlockStore)
const { items: buildingBlockItems } = storeToRefs(buildingBlockStore)

const previousView = computed(() =>
  currentCalculationItem.value?.status === STATUS_CHOICES.DRAFT ? 'project' : 'calculation'
)

const hasBlockingUnitMismatches = computed(() => {
  return quantityBuildingBlockItems.value.some((quantity) => {
    const mismatch = resolveDesignLinkUnitMismatch(quantity, buildingBlockItems.value)
    return mismatch ? quantity.unit_mismatch_accepted_key !== mismatch.key : false
  })
})

const hasMissingBuildingBlockUnits = computed(() => {
  return quantityBuildingBlockItems.value.some((quantity) => {
    if (typeof quantity.building_block !== 'number') return false
    const unit = resolveBuildingBlockUnit(buildingBlockItems.value, quantity.building_block)
    return !unit || unit.trim() === ''
  })
})

const isDesignLinksCompleted = computed(() => {
  const items = quantityBuildingBlockItems.value
  return (
    items.length > 0 &&
    items.some((quantity) => quantity.building_block != null) &&
    items.every((quantity) => !quantity.is_missing_from_last_import) &&
    !hasBlockingUnitMismatches.value &&
    !hasMissingBuildingBlockUnits.value
  )
})

const isTabCompleted = (value: CalculationFormTab, isCompleted: boolean) => {
  if (value === 'DESIGN_LINKS') {
    return isDesignLinksCompleted.value
  }
  return isCompleted
}

const disableRunCalculationButton = computed(() => {
  return (
    props.tabs.some((item) => !isTabCompleted(item.value, item.isCompleted)) ||
    isReadOnly.value ||
    props.hasBlockingSwitchIssues
  )
})

const activeTab = (value: CalculationFormTab) => {
  return tab.value === value ? 'active-tab' : ''
}

const disableTab = (value: CalculationFormTab) => {
  if (!props.hasBlockingSwitchIssues) return false
  return !isDatabaseSwitchRecoveryTab(value)
}

const completeTab = (value: boolean) => {
  return value || isReadOnly.value ? 'font-weight-bold' : 'font-weight-regular'
}

const navigateToPreviousView = () => {
  if (previousView.value === 'project') {
    router.push({ name: 'project-detail', params: { id: currentCalculationItem.value?.project } })
  } else {
    router.push({
      name: 'calculation-result',
      params: {
        id: currentCalculationItem.value?.project,
        calculationId: currentCalculationItem.value?.id
      }
    })
  }
}

const RunCalculation = async () => {
  if (!currentCalculationItem.value) return

  const hasUnlinkedRows = quantityBuildingBlockItems.value.some(
    (quantity) => quantity.building_block == null
  )
  if (hasUnlinkedRows) {
    const proceed = await confirmDeleteStore.confirm({
      title: 'Not all design links are made',
      message:
        'Some quantity rows are not linked to a building block. Unlinked rows are excluded from ' +
        'the calculation, which affects the result. Run anyway?',
      confirmText: 'Run anyway',
      cancelText: 'Cancel',
      confirmColor: 'primary'
    })
    if (!proceed) return
  }

  const response = await calculationStore.runCalculation(
    currentCalculationItem.value.id,
    currentCalculationItem.value.project
  )
  if (response) {
    router.push({
      name: 'calculation-result',
      params: {
        id: currentCalculationItem.value?.project,
        calculationId: currentCalculationItem.value?.id
      }
    })
  }
}

const isReadOnly = computed(() => {
  return currentCalculationItem.value?.status !== STATUS_CHOICES.DRAFT
})

onMounted(() => {
  drawer.value.shown = true
  calculationStore.resetAll()
})
</script>

<template>
  <v-navigation-drawer v-model="drawer.shown">
    <v-list class="py-2">
      <v-list-item-action class="d-flex justify-center py-2">
        <v-btn
          class="text-none my-1 mx-2"
          variant="tonal"
          prepend-icon="mdi-arrow-left"
          @click.stop="navigateToPreviousView"
        >
          Back to {{ previousView }}
        </v-btn>
      </v-list-item-action>

      <v-list-item class="px-0" color="primary">
        <v-divider />
        <v-tabs
          v-model="tab"
          bg-color="transparent"
          color="primary"
          direction="vertical"
          style="z-index: 1"
        >
          <template v-for="(item, index) in tabs" :key="item.value">
            <v-tab
              v-if="item.isShown"
              :value="item.value"
              :class="['text-none', activeTab(item.value)]"
              rounded="0"
              :disabled="disableTab(item.value)"
            >
              <span
                :class="[
                  'px-4',
                  activeTab(item.value),
                  completeTab(isTabCompleted(item.value, item.isCompleted))
                ]"
              >
                {{ item.label }}
              </span>
              <v-icon
                v-if="isTabCompleted(item.value, item.isCompleted) || isReadOnly"
                color="primary"
              >
                mdi-check
              </v-icon>
            </v-tab>
            <v-divider v-if="index < tabs.length - 1"></v-divider>
          </template>
        </v-tabs>
        <v-divider />
      </v-list-item>

      <v-list-item-action class="d-flex justify-center py-2">
        <v-btn
          class="text-none my-1 mx-2"
          :variant="disableRunCalculationButton ? 'tonal' : 'flat'"
          :color="disableRunCalculationButton ? 'revert' : 'primary'"
          :disabled="disableRunCalculationButton"
          @click.stop="RunCalculation"
        >
          Run calculation
        </v-btn>
      </v-list-item-action>
    </v-list>
  </v-navigation-drawer>
</template>

<style scoped>
.v-tab :deep(.v-tab__slider) {
  display: none;
}

.v-tab :deep(.v-btn__overlay) {
  display: none;
}

.active-tab {
  background-color: #ccecee;
  color: #303030;
}
</style>
